import { debounce } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Operator } from 'react-querybuilder';
import { isNullish, keys, uniqueBy } from 'remeda';
import { AutoCompleteOption, FormField } from 'src/components';
import type { IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  ActionType,
  BehaviorConfig,
  BehaviorConfigOption,
  BehaviorConfigOptionsQuery,
  BehaviorTrigger,
  ConditionType,
  ConfigActionField,
  ConfigActions,
  ConfigActionType,
  ConfigConditions,
  ConfigCustomActionAttribute,
  ConfigDataAction,
  ConfigDataCondition,
  ConfigOperator,
  ConfigTrigger,
  conversationTypeOptions,
  FormControlType,
  MacroConfig,
  PartnerGroupRules,
  PreparedConversationParticipantOption,
  RuleControlType,
  SelectorValue,
  TriggerPlaceholder,
  useBehaviorConfig,
  useBrandOptions,
  useConversationAssigneeGroupsOptions,
  useConversationChannelTypeOptions,
  useConversationParticipantOptions,
  useConversationStatusOptions,
  useConversationUrgencyOptions,
  useGroupRules,
  useLabelsOptions,
  useMembershipOptions,
  usePartnerGroupsOptions,
  usePartnersOptions,
  useProjectStages,
  UserGroupRules,
  useSelectedPartner,
  useServiceLevels,
} from 'src/graphql';
import { mapClient } from 'src/graphql/entities/client/client.mapping';
import { buildServiceLevelFilterOptions } from 'src/graphql/entities/custom-view/custom-view-config/service-level-filter';
import { onlyPublicsFilter } from 'src/graphql/entities/label/label';
import {
  useCurrentUserProfile,
  usePlatformUsersOptions,
} from 'src/graphql/entities/platform-user';
import { useProjectLostReasons } from 'src/graphql/entities/project-lost-reason';
import { useSearchAddresses } from 'src/graphql/entities/search/search-address';
import { useSearchClients } from 'src/graphql/entities/search/search-client';
import { SignalRulesConfig } from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { useVisibleLabelOptions } from 'src/graphql/entities/visible-label';
import { useGlobalStore } from 'src/store';
import { BOOLEAN_CONTROL_OPTIONS } from './rules-form-config';

export type TriggerAwareOperator = Operator & { triggers?: BehaviorTrigger[] };

export type OperatorsMap = Record<FormControlType, TriggerAwareOperator[]>;

export enum SupportedActionQueries {
  getBrand = 'getBrand',
  getConversationStatus = 'getConversationStatus',
  getConversationChannelType = 'getConversationChannelType',
  getEnabledConversationAssigneeGroups = 'getEnabledConversationAssigneeGroups',
  getEnabledConversationUrgencies = 'getEnabledConversationUrgencies',
  getMembership = 'getMembership',
  getPartnerGroup = 'getPartnerGroup',
  getPlatformUser = 'getPlatformUser',
  getAddress = 'getAddress',
  getVisibleLabels = 'getVisibleLabels',
  getPlatformUsersForSubAssignees = 'getPlatformUsersForSubAssignees',
  getServiceLevelOptions = 'getServiceLevelOptions',
  getServiceLevelIdOptions = 'getServiceLevelIdOptions',
  getConversationParticipantOptions = 'getConversationParticipantOptions',
  getConversationType = 'getConversationType',
  getPartner = 'getPartner',
  getSla = 'getSla',
  getProjectStage = 'getProjectStage',
  getProjectLostReason = 'getProjectLostReason',
  getBooleanOptions = 'getBooleanOptions',
  getAllLabels = 'getAllLabels',
  getClient = 'getClient',
}

const SUPPORTED_CONDITION_TYPES = {
  [ConditionType.Data]: 'data',
  [ConditionType.ContactCenterHours]: 'contactCenterHours',
  [ConditionType.PartnerBusinessHours]: 'partnerBusinessHours',
  [ConditionType.CustomSchedule]: 'customSchedule',
  [ConditionType.Actor]: 'actor',
  [ConditionType.PartnerSetting]: 'partnerSetting',
} as Record<ConditionType, keyof ConfigConditions>;

const SUPPORTED_ACTION_TYPES = new Set([
  ActionType.Data,
  ActionType.SendSlackMessage,
  ActionType.SendConversationMessage,
  ActionType.Webhook,
  ActionType.ManageConversationLabels,
  ActionType.ManageSubAssignees,
  ActionType.Signal,
  ActionType.CreateTwilioTask,
  ActionType.RunTriage,
  ActionType.DeleteConversation,
  ActionType.BlockConversationRequester,
  ActionType.CreateConversationMessageDraft,
  ActionType.EndSmsSession,
]);

const ACTION_TYPE_ALLOWED_RULES: Partial<
  Record<ActionType, (UserGroupRules | PartnerGroupRules)[]>
> = {
  [ActionType.ManageSubAssignees]: [UserGroupRules.AccessToManageSubAssignees],
  [ActionType.DeleteConversation]: [UserGroupRules.AccessToDeleteConversations],
  [ActionType.BlockConversationRequester]: [UserGroupRules.AccessToManageBlockedEmails],
  [ActionType.CreateConversationMessageDraft]: [PartnerGroupRules.AccessToSuggestedReplies],
  // Partner rule, re-checked live: a cached behaviorConfig survives a partner switch.
  [ActionType.EndSmsSession]: [PartnerGroupRules.SmsSessionsAvailable],
};

const OPTIONAL_ACTION_ATTRIBUTES: Partial<Record<ActionType, Set<string>>> = {
  [ActionType.RunTriage]: new Set(['userInstructions', 'conversationTriageId']),
};

type CustomActionField = FormField & {
  fieldOptions?: BehaviorConfigOption[];
  /**
   * Kept on the field so the placeholder can be resolved once the rule's trigger is
   * known — the trigger lives in the form, not in the config this hook builds.
   */
  placeholderByTrigger?: TriggerPlaceholder[];
};

export type ActionTypeOption = ConfigActionType<ActionType> & {
  triggers: BehaviorTrigger[];
};

export interface ConfigActionFields {
  [ActionType.Data]: SelectorValue[];
  [ActionType.SendSlackMessage]: CustomActionField[];
  [ActionType.SendConversationMessage]: CustomActionField[];
  [ActionType.Webhook]: CustomActionField[];
  [ActionType.ManageConversationLabels]: CustomActionField[];
  [ActionType.ManageSubAssignees]: CustomActionField[];
  [ActionType.Signal]: CustomActionField[];
  [ActionType.CreateTwilioTask]: CustomActionField[];
  [ActionType.RunTriage]: CustomActionField[];
  [ActionType.DeleteConversation]: CustomActionField[];
  [ActionType.BlockConversationRequester]: CustomActionField[];
  [ActionType.CreateConversationMessageDraft]: CustomActionField[];
  [ActionType.EndSmsSession]: CustomActionField[];
}
export interface RulesEngineConfig {
  triggers: ConfigTrigger[];
  conditions: SelectorValue[];
  actionTypes: ActionTypeOption[];
  actions: ConfigActionFields;
  timezone?: string;
  queryOptions?: UseQueryOptions;
}

const DEFAULT_CONFIG: RulesEngineConfig = {
  triggers: [],
  conditions: [],
  actionTypes: [],
  actions: {} as ConfigActionFields,
  timezone: undefined,
};

export function useRulesEngine(): RulesEngineConfig {
  const { selectedPartner } = useSelectedPartner();

  const { behaviorConfig } = useBehaviorConfig();
  const { checkValidity } = useGroupRules();
  const queryOptions = useQueryOptions(behaviorConfig);

  const operators = useMemo(() => {
    if (!behaviorConfig) return {} as OperatorsMap;

    const configOperators = behaviorConfig.operators;

    return keys.strict(configOperators).reduce((acc, operatorType) => {
      return {
        ...acc,
        [operatorType]: configOperators[operatorType].map(mapOperator),
      };
    }, {} as OperatorsMap);
  }, [behaviorConfig]);

  const triggers = useMemo(() => {
    if (!behaviorConfig) return [] as ConfigTrigger[];

    return behaviorConfig.triggers;
  }, [behaviorConfig]);

  const conditions = useMemo(() => {
    if (!behaviorConfig) return [] as SelectorValue[];

    const result = [] as SelectorValue[];
    for (const conditionType in SUPPORTED_CONDITION_TYPES) {
      if (conditionType in behaviorConfig.conditions) {
        const currentConditions =
          behaviorConfig.conditions[conditionType as keyof ConfigConditions];
        result.push(
          ...currentConditions.map((item) => {
            return mapCondition({
              condition: item,
              conditionType: conditionType as ConditionType,
              operators: operators[item.uiType],
              queryOptions,
            });
          }),
        );
      }
    }

    return result;
  }, [behaviorConfig, queryOptions, operators]);

  const { actionTypes, actions } = useMemo(() => {
    if (!behaviorConfig) {
      return {
        actionTypes: [] as ActionTypeOption[],
        actions: {} as ConfigActionFields,
      };
    }
    const actionTypes = behaviorConfig.actionTypes
      .filter((action) => {
        if (!SUPPORTED_ACTION_TYPES.has(action.value)) return false;
        const requiredRules = ACTION_TYPE_ALLOWED_RULES[action.value];
        if (requiredRules && !checkValidity(requiredRules)) return false;
        return true;
      })
      // The backend states per action which triggers it supports — Site Created, for one,
      // accepts only Webhook. Without this the dropdown offers actions that cannot run.
      .map((action) => ({
        ...action,
        triggers: getActionTriggers(behaviorConfig.actions[action.value]),
      }));

    const actions = [...SUPPORTED_ACTION_TYPES.values()].reduce((acc, actionType) => {
      const actions = behaviorConfig.actions[actionType];

      if (!actions) return acc;

      if (actionType === ActionType.Data) {
        return {
          ...acc,
          [actionType]: (actions as ConfigDataAction[]).map((item) => ({
            ...mapAction(item, actionType),
            ...getSelectorOptions(item, queryOptions),
          })) as SelectorValue[],
        };
      }

      const actionAttributes = Array.isArray(actions)
        ? actions
        : [actions].flatMap<ConfigCustomActionAttribute>((item) => item.attributes);

      return {
        ...acc,
        [actionType]: actionAttributes.map((item) => {
          const queryData = getSelectorOptions(item, queryOptions);
          const mappedAction = mapAction(item, actionType);
          let options = queryData.fieldOptions;

          if ('options' in mappedAction) {
            options = mappedAction.options as BehaviorConfigOption[];
          }
          if ('fieldOptions' in mappedAction) {
            options = mappedAction.fieldOptions;
          }

          return {
            ...mappedAction,
            ...('name' in item && OPTIONAL_ACTION_ATTRIBUTES[actionType]?.has(item.name)
              ? { required: false }
              : {}),
            options,
            fieldOptions: options,
          };
        }),
      };
    }, {} as ConfigActionFields);

    return { actionTypes, actions };
  }, [behaviorConfig, queryOptions, checkValidity]);

  const config = useMemo(
    () => ({
      triggers,
      conditions,
      actionTypes,
      actions,
      timezone: selectedPartner?.timeZone ? selectedPartner.timeZone : undefined,
      queryOptions,
    }),
    [triggers, conditions, actionTypes, actions, selectedPartner?.timeZone, queryOptions],
  );

  if (!behaviorConfig) return DEFAULT_CONFIG;

  return config;
}

/**
 * Data actions come as a list of attributes, each with its own triggers; every other
 * action type is a single object. Both shapes answer the same question: which triggers
 * may use this action.
 */
function getActionTriggers(
  action: ConfigActions[keyof ConfigActions] | undefined,
): BehaviorTrigger[] {
  if (!action) return [];

  return Array.isArray(action)
    ? [...new Set(action.flatMap((item) => item.triggers ?? []))]
    : (action.triggers ?? []);
}

export function filterByTrigger(selectedTrigger: BehaviorTrigger) {
  return function (item: { triggers: BehaviorTrigger[] }) {
    return item.triggers.includes(selectedTrigger);
  };
}

export function mapCondition({
  condition,
  conditionType,
  operators,
  queryOptions,
}: {
  condition: ConfigDataCondition;
  conditionType: ConditionType;
  operators: Operator[];
  queryOptions: QueryOptions;
}): SelectorValue {
  return {
    value: condition.attribute,
    label: `${condition.group} / ${condition.attributeLabel}`,
    uiType: condition.uiType,
    conditionType,
    controlType: RuleControlType.ConditionControl,
    triggers: condition.triggers,
    operators: condition.operators || operators,
    ...(condition.group ? { fieldType: 'groupedField', group: condition.group } : {}),
    optionsQuery: condition.optionsQuery?.graphqlQuery,
    ...getSelectorOptions(condition, queryOptions),
  } as SelectorValue;
}

const UI_TYPE_TO_DYNAMIC_FORM_TYPE = {
  [FormControlType.Text]: 'textfield',
  [FormControlType.Url]: 'textfield',
  [FormControlType.List]: 'autocomplete',
  [FormControlType.Textarea]: 'textfield',
  [FormControlType.Boolean]: 'autocomplete',
  [FormControlType.RichText]: 'rich-text',
  [FormControlType.Date]: 'textfield',
  [FormControlType.DateTime]: 'textfield',
  [FormControlType.Number]: 'textfield',
  [FormControlType.ToggleGroup]: 'toggle-group',
} as const;

/**
 * Icons for toggle-group options, matched by option value. Deliberately here rather
 * than on the backend config: `BehaviorConfigOption` is a shared value/label pair used
 * by every action, and the sparkle is a detail of this control's appearance.
 * The four-point sparkle is the lucide one, same as the macros toggle.
 */
const TOGGLE_GROUP_OPTION_ICONS: Record<string, { icon: string; iconLibrary: IconLibrary }> = {
  static: { icon: 'scroll-text', iconLibrary: 'lucide' },
  ai: { icon: 'sparkles', iconLibrary: 'lucide' },
};

export function mapAction(
  action: ConfigActionField,
  actionType: ActionType,
): SelectorValue | FormField {
  if (isDataConfigAction(action, actionType)) {
    return {
      value: action.attribute,
      label: `${action.group} / ${action.attributeLabel}`,
      ...(action.group ? { fieldType: 'groupedField', group: action.group } : {}),
      uiType: action.uiType,
      controlType: RuleControlType.ActionControl,
      optionsQuery: action.optionsQuery?.graphqlQuery,
    } as SelectorValue;
  }

  return {
    type: UI_TYPE_TO_DYNAMIC_FORM_TYPE[action.uiType],
    name: action.name,
    label: action.label,
    'data-testid': `rules-action-field-${action.name}`,
    required: `${action.label} is required`,
    defaultValue: '',
    dependsOn: action.dependsOn,
    ...('placeholderByTrigger' in action
      ? { placeholderByTrigger: action.placeholderByTrigger }
      : {}),
    ...getAdditionalFieldProps(action.uiType, action),
  } as FormField;
}

function getAdditionalFieldProps(
  uiType: FormControlType,
  action: ConfigCustomActionAttribute,
): Partial<FormField> {
  const sx = {
    width: 'auto',
    minWidth: '15rem',
    alignSelf: 'flex-start',
  };

  switch (uiType) {
    case FormControlType.List:
      return {
        sx,
        multiple: action.multiple,
        textFieldProps: {
          placeholder: action.placeholder,
          variant: 'outlined',
        },
      };
    case FormControlType.Text:
      return {
        sx,
        textFieldProps: {
          placeholder: action.placeholder,
          variant: 'outlined',
        },
      };
    case FormControlType.Textarea:
      return {
        textFieldProps: {
          placeholder: action.placeholder,
          multiline: true,
          variant: 'outlined',
          minRows: 5,
        },
      };
    case FormControlType.RichText:
      return {
        placeholder: action.placeholder,
        withSubmitButton: false,
      };
    case FormControlType.Boolean:
      return {
        sx,
        textFieldProps: {
          placeholder: action.placeholder,
          variant: 'outlined',
        },
        options: BOOLEAN_CONTROL_OPTIONS as AutoCompleteOption[],
      };
    case FormControlType.ToggleGroup:
      return {
        options: (action.options ?? []).map((option) => ({
          id: option.value,
          value: option.value,
          label: option.label,
          ...TOGGLE_GROUP_OPTION_ICONS[option.value],
        })),
      };
    default:
      return {};
  }
}

function isDataConfigAction(
  action: ConfigActionField,
  actionType: ActionType,
): action is ConfigDataAction {
  return action && actionType === ActionType.Data;
}

function mapOperator(operator: ConfigOperator): TriggerAwareOperator {
  return {
    name: operator.value,
    value: operator.value,
    label: operator.label,
    arity: operator.arity ?? undefined,
    triggers: operator.triggers ?? undefined,
  };
}

/**
 * Some operators are offered only for certain triggers — Changed To / From need a previous
 * state, so they exist on the update triggers alone. The backend says so per operator; the
 * form applies it once the rule's trigger is known.
 */
export function withTriggerOperators(
  fields: SelectorValue[],
  trigger?: BehaviorTrigger,
): SelectorValue[] {
  if (!trigger) return fields;

  return fields.map((field) => ({
    ...field,
    operators: field.operators?.filter((operator) => {
      const triggers = (operator as TriggerAwareOperator).triggers;
      return !triggers?.length || triggers.includes(trigger);
    }),
  }));
}

export interface QueryOptionsValue {
  options: BehaviorConfigOption[];
  onInputChange?: (query: string) => void;
  isLoading?: boolean;
  // we map rule values to UI-friendly labels via options,
  // but, some fields allow dynamic, searchable options, which can change
  // whenever the user searches again. This means previously retrieved items
  // (needed to label existing rule values) might drop out of the current list.
  // To avoid losing them, we maintain a 'historicalOptions' collection that
  // preserves any previously seen options for accurate rule mapping.
  historicalOptions?: BehaviorConfigOption[];
}

export type QueryOptions = Record<string, QueryOptionsValue>;

export function getSelectorOptions(
  config: {
    options?: BehaviorConfigOption[] | null;
    optionsQuery?: BehaviorConfigOptionsQuery | null;
  },
  queryOptions: QueryOptions,
): { options: BehaviorConfigOption[]; fieldOptions: BehaviorConfigOption[] } {
  if (config.options) {
    const items = config.options.map((item) => ({
      ...item,
      // Backend provides {label,value} model for options, but maybe ids will appear eventually.
      id: item.id ?? item.value,
    }));
    return {
      options: items,
      fieldOptions: items,
    };
  }

  if (config.optionsQuery && queryOptions) {
    const items = queryOptions[config.optionsQuery.graphqlQuery]?.options ?? [];
    return {
      options: items,
      fieldOptions: items,
    };
  }

  return { options: [], fieldOptions: [] };
}

const filtersForUsePartnerGroup = [
  {
    attribute: 'description',
    operator: '!=',
    value: '',
  },
];

const SLA_OPTIONS = [
  {
    value: '10',
    label: '10',
    id: '10',
  },
  {
    value: '30',
    label: '30',
    id: '30',
  },
  {
    value: '60',
    label: '60',
    id: '60',
  },
];

const YES_NO_OPTIONS = [
  {
    id: 'yes',
    label: 'Yes',
    value: 'Yes',
    icon: 'check',
    iconLibrary: 'lucide',
    color: 'success.main',
  },
  {
    id: 'no',
    label: 'No',
    value: 'No',
    icon: 'x',
    iconLibrary: 'lucide',
    color: 'error.main',
  },
];

export function useQueryOptions(
  config: BehaviorConfig | MacroConfig | SignalRulesConfig | null,
): QueryOptions {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { checkValidity } = useGroupRules();
  const canManageSubAssignees = checkValidity([UserGroupRules.AccessToManageSubAssignees]);
  const requiredQueries = useMemo(() => {
    const uniqueQueries = new Set();

    if (!config) return uniqueQueries;

    if ('conditions' in config) {
      for (const condition in SUPPORTED_CONDITION_TYPES) {
        const conditionTypeKey = SUPPORTED_CONDITION_TYPES[condition as ConditionType];
        const conditions = config.conditions[conditionTypeKey] || [];

        conditions.forEach((condition) => {
          if (condition.optionsQuery) {
            uniqueQueries.add(condition.optionsQuery.graphqlQuery);
          }
        });
      }
    }

    if ('actions' in config) {
      for (const actionType of SUPPORTED_ACTION_TYPES) {
        if (actionType !== ActionType.Data) break;

        const actions = config.actions[actionType];
        actions.forEach((action) => {
          if (action.optionsQuery) {
            uniqueQueries.add(action.optionsQuery.graphqlQuery);
          }
        });
      }

      // Specific logic to support ManageConversationLabels action,
      // relies on graphql query to get visible labels.
      const manageConversationLabelsActionAttributes =
        config.actions[ActionType.ManageConversationLabels].attributes;
      if (manageConversationLabelsActionAttributes?.length) {
        const labelIdsAttribute = manageConversationLabelsActionAttributes.find(
          (attribute) => attribute.name === 'labelIds',
        )!;

        uniqueQueries.add(labelIdsAttribute.optionsQuery.graphqlQuery);
      }

      // Specific logic to support ManageSubAssignees action,
      // relies on graphql query to get active platform users for sub-assignees.
      // Skip for users without the rule since the action type is hidden for them.
      const manageSubAssigneesActionAttributes =
        config.actions[ActionType.ManageSubAssignees]?.attributes;
      if (canManageSubAssignees && manageSubAssigneesActionAttributes?.length) {
        const platformUserIdsAttribute = manageSubAssigneesActionAttributes.find(
          (attribute) => attribute.name === 'platformUserIds',
        );
        if (platformUserIdsAttribute?.optionsQuery) {
          uniqueQueries.add(platformUserIdsAttribute.optionsQuery.graphqlQuery);
        }
      }
    }

    return uniqueQueries;
  }, [config, canManageSubAssignees]);

  return useOptionsQueries({
    partnerId,
    enabledQueries: requiredQueries as Set<SupportedActionQueries>,
  });
}

export function useOptionsQueries({
  partnerId,
  enabledQueries,
  area = 'behaviors',
  concreteCurrentUserOption = false,
}: {
  partnerId: number | null;
  enabledQueries?: Set<SupportedActionQueries>;
  area?: 'behaviors' | 'custom_views';
  // bulk actions assign a concrete user now, so they opt into the real id instead of the {{user.id}} token
  concreteCurrentUserOption?: boolean;
}): QueryOptions {
  const { user } = useCurrentUserProfile();
  const { groupRules } = useGroupRules();

  const canAccessPrivateLabels = groupRules[UserGroupRules.AccessToPrivateLabels] as boolean;

  const visibleLabelFilters = useMemo(
    () => (canAccessPrivateLabels ? [] : [onlyPublicsFilter]),
    [canAccessPrivateLabels],
  );

  const platformUserFilters = useMemo(() => {
    return area === 'behaviors'
      ? [
          {
            attribute: 'userGroups/ruleValues/userGroupRuleId',
            operator: '=',
            value: UserGroupRules.AccessToUpdateConversation,
          },
        ]
      : [];
  }, [area]);

  // For now, supported queries for options should be provided explicitly.
  // In future, we may think on building dynamic graphql queries, but it will take more time.
  const { allUserRecords } = usePlatformUsersOptions(
    {
      partnerId,
      filters: [
        ...platformUserFilters,
        {
          attribute: 'isActive',
          operator: '=',
          value: 'true',
        },
      ],
      removeNestedFilters: false,
    },
    {
      skip:
        enabledQueries &&
        !enabledQueries.has(SupportedActionQueries.getPlatformUser) &&
        !enabledQueries.has(SupportedActionQueries.getConversationParticipantOptions),
    },
  );

  const platformUserOptions = useMemo(() => {
    if (area !== 'custom_views' || !user?.id) {
      return allUserRecords;
    }

    const otherUserOptions = allUserRecords.filter((option) => option.id !== user.id);

    const meOption = concreteCurrentUserOption
      ? { id: user.id, value: user.id }
      : { id: '{{user.id}}', value: '{{user.id}}' };

    return [
      {
        ...meOption,
        firstName: user.firstName,
        lastName: user.lastName,
        label: 'Me',
      },
      ...otherUserOptions,
    ];
  }, [allUserRecords, area, user, concreteCurrentUserOption]);

  const assigneeGroupOptions = useConversationAssigneeGroupsOptions(partnerId, {
    skip:
      enabledQueries &&
      !enabledQueries.has(SupportedActionQueries.getEnabledConversationAssigneeGroups),
  });

  const conversationStatusOptions = useConversationStatusOptions({
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getConversationStatus),
  });

  const conversationUrgencyOptions = useConversationUrgencyOptions(partnerId!, {
    skip:
      enabledQueries &&
      !enabledQueries.has(SupportedActionQueries.getEnabledConversationUrgencies),
  });

  const membershipOptions = useMembershipOptions({
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getMembership),
  });

  const conversationLabelOptions = useVisibleLabelOptions({
    skip:
      area === 'custom_views' ||
      (enabledQueries && !enabledQueries.has(SupportedActionQueries.getVisibleLabels)),
  });

  const { allUserRecords: subAssigneeUserOptions } = usePlatformUsersOptions(
    {
      partnerId,
      filters: [
        {
          attribute: 'isActive',
          operator: '=',
          value: 'true',
        },
      ],
      removeNestedFilters: false,
    },
    {
      skip:
        enabledQueries &&
        !enabledQueries.has(SupportedActionQueries.getPlatformUsersForSubAssignees),
    },
  );

  const { labelOptions: customViewVisibleLabelOptions } = useLabelsOptions({
    partnerId,
    labelsFilters: visibleLabelFilters,
    labelGroupsOptions: { skip: true },
    skip:
      area !== 'custom_views' ||
      (enabledQueries && !enabledQueries.has(SupportedActionQueries.getVisibleLabels)),
  });

  const visibleLabelOptions = useMemo(
    () =>
      area === 'custom_views'
        ? customViewVisibleLabelOptions.filter((option) => option.type === 'label')
        : conversationLabelOptions,
    [area, customViewVisibleLabelOptions, conversationLabelOptions],
  );

  const { labelOptions: allLabelOptions } = useLabelsOptions({
    partnerId,
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getAllLabels),
    labelGroupsOptions: { skip: true },
  });

  const conversationChannelTypeOptions = useConversationChannelTypeOptions({
    skip:
      enabledQueries && !enabledQueries.has(SupportedActionQueries.getConversationChannelType),
  });

  const brandOptions = useBrandOptions(partnerId, {
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getBrand),
  });

  const partnerGroupsOptions = usePartnerGroupsOptions(
    {
      partnerId,
      filters: filtersForUsePartnerGroup,
    },
    {
      skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getPartnerGroup),
    },
  );

  const { projectStages } = useProjectStages({
    options: {
      skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getProjectStage),
    },
  });
  const { projectLostReasons } = useProjectLostReasons({
    options: {
      skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getProjectLostReason),
    },
  });

  // store historical values for mapping purposes
  const [historicalRequesters, setHistoricalRequesters] = useState<
    PreparedConversationParticipantOption[]
  >([]);

  const {
    getConversationParticipantOptions,
    conversationParticipantOptions,
    loading: isConversationParticipantLoading,
  } = useConversationParticipantOptions();

  useEffect(() => {
    setHistoricalRequesters((prev) => {
      return uniqueBy([...prev, ...conversationParticipantOptions], (item) => item.id);
    });
  }, [conversationParticipantOptions]);

  useEffect(() => {
    setHistoricalRequesters([]);
  }, [partnerId]);

  const onConversationRequestersInputChange = useMemo(() => {
    return debounce(
      (filter: string) =>
        getConversationParticipantOptions({ partnerId: partnerId!, searchText: filter }),
      SEARCH_DEBOUNCE_DELAY,
    );
  }, [getConversationParticipantOptions, partnerId]);

  const [addressSearchTerm, setAddressSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  // store historical values for mapping purposes
  const [historicalAddresses, setHistoricalAddresses] = useState<BehaviorConfigOption[]>([]);
  const [historicalClients, setHistoricalClients] = useState<BehaviorConfigOption[]>([]);

  const { records: addresses, isLoading: isAddressLoading } = useSearchAddresses({
    searchTerm: addressSearchTerm,
    limit: 10,
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getAddress),
  });
  const { records: clients, isLoading: isClientLoading } = useSearchClients({
    searchTerm: clientSearchTerm,
    partnerId,
    limit: 10,
    skip: enabledQueries && !enabledQueries.has(SupportedActionQueries.getClient),
  });

  const addressOptions = useMemo(() => {
    return (
      addresses?.map((i) => ({
        id: i.id,
        value: i.id,
        label: i.name,
      })) ?? []
    );
  }, [addresses]);

  const clientOptions = useMemo(() => {
    return (
      clients?.map((client) => {
        const mappedClient = mapClient(client as unknown as Parameters<typeof mapClient>[0]);
        const label = `${mappedClient.fullName}${
          mappedClient.primaryEmail ? ` (${mappedClient.primaryEmail})` : ''
        }`;

        return {
          id: mappedClient.clientId,
          value: mappedClient.clientId,
          label,
        };
      }) ?? []
    );
  }, [clients]);

  useEffect(() => {
    setHistoricalAddresses((prev) => {
      return uniqueBy([...prev, ...addressOptions], (item) => item.id);
    });
  }, [addressOptions]);

  useEffect(() => {
    setHistoricalClients((prev) => {
      return uniqueBy([...prev, ...clientOptions], (item) => item.id);
    });
  }, [clientOptions]);

  const onAddressInputChange = useMemo(() => {
    return debounce((filter: string) => setAddressSearchTerm(filter), SEARCH_DEBOUNCE_DELAY);
  }, [setAddressSearchTerm]);

  const onClientInputChange = useMemo(() => {
    return debounce((filter: string) => setClientSearchTerm(filter), SEARCH_DEBOUNCE_DELAY);
  }, [setClientSearchTerm]);

  const partnerIds = useMemo(() => {
    return !isNullish(partnerId) ? [String(partnerId)] : undefined;
  }, [partnerId]);

  const partnerOptions = usePartnersOptions(partnerIds);

  // Service Level options come from the platform_sla catalog (canonical tiers + Secure + Non-Member),
  // gated by the AccessToPlatformSLAs partner-group rule, replacing the legacy static ServiceLevel
  // enum list. `allowWhenNoPartner: true` preserves the legacy rules-engine behavior of offering the
  // tiers in the platform/admin (no-partner-selected) editing context rather than an empty dropdown.
  // - getServiceLevelOptions: value = display name; Secure stripped unless the user can set it (set context).
  // - getServiceLevelIdOptions (filter context): value = platform_sla_id, label = display name. Built from
  //   serviceLevelFilterOptions so Secure stays filterable even without set-permission (1V-5372). Non-Member
  //   is kept as the empty platform_sla bucket. A synthetic "CPP" option is appended; the custom-view
  //   filter mappers rewrite it to a cppExpDate > today condition.
  const { serviceLevelOptions: gatedServiceLevelOptions, serviceLevelFilterOptions } =
    useServiceLevels(partnerId, {
      allowWhenNoPartner: true,
    });
  const serviceLevelIdOptions = useMemo(
    () => buildServiceLevelFilterOptions(serviceLevelFilterOptions),
    [serviceLevelFilterOptions],
  );

  return useMemo<Record<SupportedActionQueries, QueryOptionsValue>>(
    () => ({
      [SupportedActionQueries.getBrand]: { options: brandOptions },
      [SupportedActionQueries.getConversationStatus]: { options: conversationStatusOptions },
      [SupportedActionQueries.getConversationChannelType]: {
        options: conversationChannelTypeOptions,
      },
      [SupportedActionQueries.getEnabledConversationAssigneeGroups]: {
        options: assigneeGroupOptions,
      },
      [SupportedActionQueries.getEnabledConversationUrgencies]: {
        options: conversationUrgencyOptions,
      },
      [SupportedActionQueries.getMembership]: { options: membershipOptions },
      [SupportedActionQueries.getPartnerGroup]: { options: partnerGroupsOptions },
      [SupportedActionQueries.getPlatformUser]: {
        options: platformUserOptions,
      },
      [SupportedActionQueries.getConversationParticipantOptions]: {
        historicalOptions: historicalRequesters,
        options: conversationParticipantOptions,
        onInputChange: onConversationRequestersInputChange,
        isLoading: isConversationParticipantLoading,
      },
      [SupportedActionQueries.getAddress]: {
        historicalOptions: historicalAddresses,
        options: addressOptions,
        onInputChange: onAddressInputChange,
        isLoading: isAddressLoading,
      },
      [SupportedActionQueries.getClient]: {
        historicalOptions: historicalClients,
        options: clientOptions,
        onInputChange: onClientInputChange,
        isLoading: isClientLoading,
      },
      [SupportedActionQueries.getVisibleLabels]: {
        options: visibleLabelOptions,
      },
      [SupportedActionQueries.getPlatformUsersForSubAssignees]: {
        options: subAssigneeUserOptions,
      },
      [SupportedActionQueries.getAllLabels]: {
        options: allLabelOptions,
      },
      [SupportedActionQueries.getServiceLevelOptions]: { options: gatedServiceLevelOptions },
      [SupportedActionQueries.getServiceLevelIdOptions]: { options: serviceLevelIdOptions },
      [SupportedActionQueries.getConversationType]: { options: conversationTypeOptions },
      [SupportedActionQueries.getPartner]: {
        options: partnerOptions,
      },
      [SupportedActionQueries.getProjectStage]: {
        options: projectStages,
      },
      [SupportedActionQueries.getProjectLostReason]: {
        options: projectLostReasons,
      },
      [SupportedActionQueries.getSla]: {
        options: SLA_OPTIONS,
      },
      [SupportedActionQueries.getBooleanOptions]: {
        options: YES_NO_OPTIONS,
      },
    }),
    [
      brandOptions,
      conversationStatusOptions,
      conversationChannelTypeOptions,
      assigneeGroupOptions,
      conversationUrgencyOptions,
      membershipOptions,
      partnerGroupsOptions,
      platformUserOptions,
      historicalRequesters,
      conversationParticipantOptions,
      isConversationParticipantLoading,
      onConversationRequestersInputChange,
      historicalAddresses,
      addressOptions,
      isAddressLoading,
      onAddressInputChange,
      historicalClients,
      clientOptions,
      onClientInputChange,
      isClientLoading,
      visibleLabelOptions,
      subAssigneeUserOptions,
      allLabelOptions,
      gatedServiceLevelOptions,
      serviceLevelIdOptions,
      partnerOptions,
      projectStages,
      projectLostReasons,
    ],
  );
}

export type UseQueryOptions = ReturnType<typeof useQueryOptions>;

type ActionFields = ConfigActionFields[keyof ConfigActionFields];

/**
 * Swaps in the trigger-specific placeholder, so a Site rule shows a Site webhook body
 * example instead of the Conversation one. Fields without a match are left untouched,
 * which covers every action type that carries no per-trigger example.
 */
export function withTriggerPlaceholder(
  fields: ActionFields = [],
  trigger?: BehaviorTrigger,
): ActionFields {
  if (!trigger) return fields;

  return (fields as CustomActionField[]).map((field) => {
    const placeholder = field.placeholderByTrigger?.find(
      (item) => item.trigger === trigger,
    )?.placeholder;

    if (!placeholder) return field;

    return 'textFieldProps' in field
      ? { ...field, textFieldProps: { ...field.textFieldProps, placeholder } }
      : { ...field, placeholder };
  }) as ActionFields;
}
